/**
 * StaffSessions — active sign-in sessions of one staff member, with the
 * account-takeover response: END EVERY SESSION (2026-08-07, Pat).
 *
 * Lives in the staff editor's "Account & role" section, next to "Can sign in?"
 * — the security facts of the account sit together. Ops: listStaffSessions /
 * revokeStaffSession / revokeStaffSessions (api-contracts/src/staff.ts).
 *
 * Honesty rules kept: the list only ever shows sessions the serving path
 * returns (no invented devices); a member who cannot sign in says so instead
 * of rendering an empty table; every write awaits its response before the
 * toast (UIR-21 class), and a failed revoke says nothing was ended.
 *
 * The confirm is a <Modal> per the overlay standard — one glance, two buttons,
 * settle it now — and it names the consequence ("… will be signed out of N
 * devices and must sign in again") rather than asking "are you sure?".
 */
import { useCallback, useEffect, useState } from "react";
import { Btn, Checkbox, Icon, Pill, T, alpha } from "@v0y4ge/jrni-ui";
import type { StaffSession } from "@v0y4ge/api-contracts";
import { listStaffSessions, revokeStaffSession, revokeStaffSessions } from "@v0y4ge/api-client";
import { Modal } from "../../shell/Overlay";
import { toast } from "../../shell/toast";
import { useT } from "../../lib/i18n";
import { friendlyDateTime } from "../../lib/datetime";
import { SvcRow, SvcSelect } from "../../lib/svc-editor-primitives";

type Reason = "compromised" | "device-lost" | "offboarding" | "other";

function agoLabel(iso: string, t: (k: string, d?: string) => string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!isFinite(ms) || ms < 0) return "";
  const min = Math.round(ms / 60000);
  if (min < 1) return t("staff.sessions.justNow", "just now");
  if (min < 60) return `${min} ${t("staff.sessions.minAgo", "min ago")}`;
  const hrs = Math.round(min / 60);
  if (hrs < 24) return `${hrs} ${t("staff.sessions.hoursAgo", "h ago")}`;
  const days = Math.round(hrs / 24);
  return `${days} ${t("staff.sessions.daysAgo", "d ago")}`;
}

export default function StaffSessions({ staffId, staffName, canWrite, signsIn, onStatusChange }: {
  staffId: string;
  staffName: string;
  canWrite: boolean;
  signsIn: boolean;
  /** The revoke can also block sign-in — the editor's status field must follow. */
  onStatusChange?: (status: "active" | "on-leave" | "inactive") => void;
}) {
  const t = useT();
  const [rows, setRows] = useState<StaffSession[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [reason, setReason] = useState<Reason>("compromised");
  const [block, setBlock] = useState(true);
  const [blocked, setBlocked] = useState(false);

  const load = useCallback(() => {
    if (!staffId || !signsIn) { setRows([]); return; }
    setError(null);
    listStaffSessions(staffId)
      .then((r) => { setRows(r.data ?? []); setBlocked(!!r.signInBlocked); })
      .catch((e) => { console.warn("listStaffSessions failed:", e); setError(t("staff.sessions.couldntLoad", "Couldn't load sessions.")); });
  }, [staffId, signsIn]);

  useEffect(() => { load(); }, [load]);

  const active = (rows ?? []).filter((s) => s.status === "active");

  const endAll = async () => {
    setBusy("all");
    try {
      const r = await revokeStaffSessions(staffId, { reason, blockSignIn: block });
      setRows(r.data ?? []);
      setBlocked(!!r.signInBlocked);
      if (r.status) onStatusChange?.(r.status);
      setConfirm(false);
      toast({
        tone: "success",
        title: t("staff.sessions.endedAll", "Signed out everywhere"),
        subtitle: `${r.revokedCount} ${t("staff.sessions.endedAllSub", "session(s) ended — the next request from each device is rejected.")}`,
      });
    } catch (e) {
      console.warn("revokeStaffSessions failed:", e);
      toast({ tone: "danger", title: t("staff.sessions.endFailed", "Couldn't end the sessions"), subtitle: t("staff.sessions.endFailedSub", "Nothing was ended — retry, and escalate if it keeps failing.") });
    } finally { setBusy(null); }
  };

  const endOne = async (s: StaffSession) => {
    setBusy(s.id);
    try {
      const r = await revokeStaffSession(staffId, s.id);
      setRows(r.data ?? []);
      toast({ tone: "success", title: t("staff.sessions.endedOne", "Session ended"), subtitle: s.device });
    } catch (e) {
      console.warn("revokeStaffSession failed:", e);
      toast({ tone: "danger", title: t("staff.sessions.endFailed", "Couldn't end the sessions") });
    } finally { setBusy(null); }
  };

  const rowStyle = { display: "flex", alignItems: "center", gap: 12, padding: "11px 13px", border: `1px solid ${T.border}`, borderRadius: 10, background: T.white } as const;

  return (
    <SvcRow label={t("staff.sessions.label", "Active sessions")} hint={t("staff.sessions.hint", "Every device this person is currently signed in on. End them all if the account may be compromised — the credential keeps working until you do.")} full>
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }} data-testid="staff-sessions">
        {!signsIn ? (
          <div style={{ padding: "16px 14px", textAlign: "center", color: T.soft, fontSize: 13, border: `1.5px dashed ${T.border}`, borderRadius: 10 }}>
            {t("staff.sessions.noLogin", "This person has no login, so there is nothing to sign out.")}
          </div>
        ) : error ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 10, background: "#FDECEC", border: "1px solid #F5B5B5", fontSize: 13, color: T.n1 }}>
            <Icon.warn size={15} /><span style={{ flex: 1 }}>{error}</span>
            <Btn variant="secondary" size="sm" onClick={load}>{t("common.actions.retry", "Retry")}</Btn>
          </div>
        ) : rows === null ? (
          <div style={{ fontSize: 13, color: T.soft, padding: "10px 2px" }}>{t("staff.sessions.loading", "Loading sessions…")}</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: "16px 14px", textAlign: "center", color: T.soft, fontSize: 13, border: `1.5px dashed ${T.border}`, borderRadius: 10 }}>
            {t("staff.sessions.none", "No active sessions.")}
          </div>
        ) : rows.map((s) => {
          const gone = s.status === "revoked";
          return (
            <div key={s.id} style={{ ...rowStyle, opacity: gone ? 0.62 : 1 }} data-testid="staff-session-row">
              <span style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0, background: alpha(gone ? T.n3 : T.p1, "1f"), color: gone ? T.n3 : T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <Icon.browser size={15} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {s.device} · {s.client}
                </div>
                <div style={{ fontSize: 11.5, color: T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {[s.location, s.ipAddress].filter(Boolean).join(" · ") || t("staff.sessions.unknownOrigin", "Origin unknown")}
                  {" · "}
                  {gone
                    ? `${t("staff.sessions.endedAt", "ended")} ${friendlyDateTime(s.revokedAt ?? s.lastSeenAt)}`
                    : `${t("staff.sessions.lastActive", "last active")} ${agoLabel(s.lastSeenAt, t)}`}
                </div>
              </div>
              {gone ? <Pill tone="neutral">{t("staff.sessions.revoked", "Ended")}</Pill> : <Pill tone="success" dot>{t("staff.sessions.active", "Active")}</Pill>}
              {!gone && canWrite ? (
                <Btn variant="secondary" size="sm" disabled={busy === s.id} onClick={() => void endOne(s)}>
                  {busy === s.id ? t("staff.sessions.ending", "Ending…") : t("staff.sessions.endOne", "End")}
                </Btn>
              ) : null}
            </div>
          );
        })}

        {signsIn ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 2 }}>
            <Btn variant="danger" size="sm" icon={<Icon.lock size={13} />} disabled={!canWrite || !active.length || busy === "all"} onClick={() => setConfirm(true)}>
              {t("staff.sessions.endAll", "Sign out of all devices")}
            </Btn>
            <span style={{ fontSize: 12, color: T.soft }}>
              {active.length
                ? `${active.length} ${t("staff.sessions.activeCount", "active session(s)")}`
                : t("staff.sessions.noneActive", "No active sessions to end.")}
            </span>
            {blocked ? <Pill tone="warn">{t("staff.sessions.signInBlocked", "Sign-in blocked")}</Pill> : null}
          </div>
        ) : null}
      </div>

      {confirm ? (
        <Modal
          title={t("staff.sessions.confirmTitle", "Sign out of all devices?")}
          sub={`${staffName || t("staff.sessions.thisPerson", "This person")} ${t("staff.sessions.confirmSub", "will be signed out of every active session and must sign in again.")}`}
          tone="danger"
          icon={<Icon.lock size={16} />}
          onClose={() => setConfirm(false)}
          testid="staff-sessions-confirm"
          footer={<>
            <Btn variant="secondary" onClick={() => setConfirm(false)}>{t("common.actions.cancel", "Cancel")}</Btn>
            <Btn variant="danger" disabled={busy === "all"} onClick={() => void endAll()}>
              {busy === "all" ? t("staff.sessions.ending", "Ending…") : `${t("staff.sessions.endAllConfirm", "End")} ${active.length} ${t("staff.sessions.sessionsWord", "session(s)")}`}
            </Btn>
          </>}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.n2, marginBottom: 6 }}>{t("staff.sessions.reason", "Reason")}</div>
              <SvcSelect value={reason} onChange={(v) => setReason(v as Reason)} options={[
                { id: "compromised", label: t("staff.sessions.reasonCompromised", "Account may be compromised") },
                { id: "device-lost", label: t("staff.sessions.reasonDevice", "Device lost or stolen") },
                { id: "offboarding", label: t("staff.sessions.reasonOffboarding", "Offboarding") },
                { id: "other", label: t("staff.sessions.reasonOther", "Other") },
              ]} />
            </div>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
              <span style={{ marginTop: 1 }}><Checkbox checked={block} onChange={() => setBlock((b) => !b)} /></span>
              <span style={{ fontSize: 13, color: T.n2, lineHeight: 1.5 }}>
                <b style={{ color: T.n1 }}>{t("staff.sessions.blockLabel", "Also block sign-in")}</b>
                <br />
                {t("staff.sessions.blockHint", "Sets the member to Inactive, so a stolen password cannot simply start a new session. Reactivate them under Status when the account is secure.")}
              </span>
            </label>
          </div>
        </Modal>
      ) : null}
    </SvcRow>
  );
}
